/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react'
import { flushSync } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchClasses,
  selectClassesByCourseID,
  selectClassesStatus,
} from 'src/v3/create/create.slice'
import {
  AddIcon,
  ClassIcon,
  DeleteForeverIcon,
  EditIcon,
  LeftArrowIcon,
  RightArrowIcon,
  SaveIcon,
  NumberedListIcon,
  BulletedListIcon,
  ImageIcon,
} from 'src/v3/core/icons'
import { Course } from 'src/v3/core/types'
import ConfirmAction from 'src/v3/components/confirm-action'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import theme from 'src/theme'

interface Props {
  course?: Course
  setStep: React.Dispatch<
    React.SetStateAction<
      'basic-info' | 'classes' | 'accreditations' | 'publishing'
    >
  >
}

let moveItem = (list: any[], oldIndex: number, newIndex: number) => {
  let without = list.filter((x, i) => i !== oldIndex)
  return [
    ...without.slice(0, newIndex),
    list[oldIndex],
    ...without.slice(newIndex),
  ]
}

export default ({ course, setStep }: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let courseId = course?.id || ''
  let classes = useAppSelector(selectClassesByCourseID(courseId))
  let classesStatus = useAppSelector(selectClassesStatus)

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [deleting, setDeleting] = useState<string>('')
  let [confirm, setConfirm] = useState<string>('')
  let [classOrder, setClassOrder] = useState<string[]>([])
  let [view, setView] = useState<'list' | 'order'>('list')
  let [saving, setSaving] = useState<boolean>(false)

  let initializedRef = useRef<string>('')

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (classesStatus === 'fulfilled' && courseId && classes.length > 0) {
      // Only initialize once per course
      if (initializedRef.current !== courseId) {
        initializedRef.current = courseId
        setClassOrder(classes.map((c) => c.id))
      }
    }
  }, [courseId, classesStatus, classes.length])

  let handleCreateClass = useCallback(() => {
    if (!course?.id) {
      flushSync(() => {
        setErrorMessage('Course information is required to create a class')
      })
      return
    }
    // Navigate to Class Wizard in creation mode
    navigate(`/create/v4/course/${course.id}/class`)
  }, [course?.id, navigate])

  let handleEditClass = useCallback(
    (classId: string) => {
      if (!course?.id) return
      // Navigate to Class Wizard in edit mode
      navigate(`/create/v4/course/${course.id}/class/${classId}`)
    },
    [course?.id, navigate],
  )

  let handleDeleteClass = async (classId: string) => {
    setDeleting(classId)
    try {
      await axios.delete(`/api/v2/create/delete-class?classID=${classId}`)
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(fetchClasses())
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data || err?.message || 'Error deleting class',
      )
    } finally {
      setDeleting('')
    }
  }

  let handleSaveClassOrder = async () => {
    if (!course?.id) return

    setSaving(true)
    try {
      await axios.put('/api/v2/create/class/sequence', {
        courseID: course.id,
        classes: classOrder,
      })
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(fetchClasses())
      setView('list')
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data || err?.message || 'Error saving class order',
      )
    } finally {
      setSaving(false)
    }
  }

  if (!course) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Course details are not available. Please complete the Basic Info step
          first to manage classes.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('basic-info')}
          >
            Back to Basic Info
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Course Classes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Create individual lessons or modules within this course. Each class has
        its own title screen, content, quiz, and storyboard.
      </Typography>
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="large"
          startIcon={<AddIcon />}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleCreateClass()
          }}
          disabled={saving}
        >
          Create New Class
        </Button>

        {classes.length > 1 && (
          <Button
            variant="outlined"
            size="large"
            startIcon={<NumberedListIcon />}
            onClick={() => setView('order')}
            disabled={view === 'order'}
          >
            Reorder Classes
          </Button>
        )}
      </Box>
      {/* Content based on view mode */}
      {view === 'list' ? (
        /* List View */
        classes.length > 0 ? (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Existing Classes ({classes.length})
            </Typography>
            <Box>
              {classes
                .sort((a, b) => a.sequence - b.sequence)
                .map((classItem) => (
                  <Box>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {/* Class Thumbnail */}
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          paddingBottom: '56.25%', // 16:9 aspect ratio
                          bgcolor: 'grey.100',
                          overflow: 'hidden',
                        }}
                      >
                        {classItem.imageName ? (
                          <img
                            src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${classItem.imageName}`}
                            alt={classItem.name}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              bgcolor: 'black',
                              color: 'text.secondary',
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 32 }} />
                            <Typography variant="caption">No Image</Typography>
                          </Box>
                        )}
                      </Box>

                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          <ClassIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6" component="div">
                            {classItem.name}
                          </Typography>
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {classItem.description}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          Sequence: {classItem.sequence}
                        </Typography>
                      </CardContent>

                      <Box
                        sx={{
                          p: 2,
                          pt: 0,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditClass(classItem.id)}
                        >
                          Edit Class
                        </Button>

                        <Tooltip title="Delete Class">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deleting === classItem.id}
                            onClick={() => setConfirm(`delete-${classItem.id}`)}
                          >
                            <DeleteForeverIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Card>
                  </Box>
                ))}
            </Box>
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mb: 4 }}>
            No classes created yet. Click "Create New Class" to add your first
            class to this course.
          </Alert>
        )
      ) : (
        /* Reorder View */
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Drag to Reorder Classes
          </Typography>
          <Box
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.primary.dark}`,
              borderRadius: 3,
              display: 'inline-block',
              minWidth: 300,
            }}
          >
            <Typography color="text.secondary" align="center" sx={{ mb: 2 }}>
              Class Order
            </Typography>

            <DragDropContext
              onDragEnd={(result) => {
                if (!result.destination) {
                  return
                }
                setClassOrder(
                  moveItem(
                    classOrder,
                    result.source.index,
                    result.destination?.index,
                  ),
                )
              }}
            >
              <Droppable droppableId="class-order">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {classOrder.map((classId, i) => {
                      let classItem = classes.find((cl) => cl.id === classId)!
                      return (
                        <Draggable
                          key={classItem.id}
                          draggableId={classItem.id || i.toString()}
                          index={i}
                        >
                          {(prov) => (
                            <Box
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                            >
                              <Chip
                                sx={{
                                  my: 1,
                                  cursor: 'grab',
                                  width: 250,
                                  justifyContent: 'flex-start',
                                }}
                                color="primary"
                                label={classItem.name}
                                avatar={<Avatar>{i + 1}</Avatar>}
                                variant="outlined"
                              />
                            </Box>
                          )}
                        </Draggable>
                      )
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveClassOrder}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Class Order'}
            </Button>

            <Button
              variant="outlined"
              startIcon={<BulletedListIcon />}
              onClick={() => setView('list')}
              disabled={saving}
            >
              Back to Class List
            </Button>
          </Box>
        </Box>
      )}
      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/create/v4')}>
            Save and Exit
          </Button>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('basic-info')}
          >
            Back to Basic Info
          </Button>
        </Box>

        <Button
          variant="contained"
          endIcon={<RightArrowIcon />}
          onClick={() => setStep('accreditations')}
        >
          Continue to Accreditations
        </Button>
      </Box>
      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}
      {/* Delete Confirmation Dialog */}
      {confirm.startsWith('delete-') && (
        <ConfirmAction
          executeAction={() => {
            let classId = confirm.replace('delete-', '')
            return handleDeleteClass(classId)
          }}
          open={confirm}
          setOpen={setConfirm}
          openText={confirm}
          title="Delete Class"
          text={`Are you sure you want to delete this class? This action cannot be undone.`}
          onFailure={(err) =>
            setErrorMessage(err?.message || 'Error deleting class')
          }
        />
      )}
    </Box>
  )
}
